// Branch-level delivery charges, admin side. The rules and messages mirror
// server/services/branchDeliveryCharges.js, which validates the same table
// again on save:
// - From and To are cart values in rupees, with at most 2 decimals, and both
//   are included in the row's range;
// - row 1 starts at ₹0 and has a finite To;
// - every next row starts exactly ₹0.01 after the previous row's To;
// - only the last row's To is Infinity, and the last row must be Infinity.
// Together these mean every cart value falls into exactly one row.

// Same Base KM choices as Configuration → Global.
export const BRANCH_DELIVERY_BASE_KM_OPTIONS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10'];
export const MAX_BRANCH_DELIVERY_CHARGE_ROWS = 20;

const AMOUNT_PATTERN = /^\d{1,9}(\.\d{1,2})?$/;

const FIELD_LABELS = {
  fromCartValue: 'From',
  toCartValue: 'To',
  baseKM: 'Base KM',
  basePrice: 'Charges',
  pricePerKM: 'Charges/KM',
};

let rowKeySequence = 0;

const toText = (value) => (value === null || value === undefined ? '' : String(value));

function parseAmount(value) {
  const text = toText(value).trim();
  return AMOUNT_PATTERN.test(text) ? Number(text) : null;
}

const toPaise = (amount) => Math.round(amount * 100);

const formatRupees = (amount) => '₹' + (Number.isInteger(amount) ? String(amount) : amount.toFixed(2));

export function newBranchDeliveryChargeRow(fromCartValue = '') {
  rowKeySequence += 1;
  return {
    key: `branch-delivery-charge-${rowKeySequence}`,
    fromCartValue,
    toCartValue: '',
    isToInfinity: false,
    baseKM: '',
    basePrice: '',
    pricePerKM: '',
  };
}

/** Saved rows (numbers, as stored on the business) as editable rows (text). */
export function branchDeliveryChargeRowsFromBusiness(rows) {
  return (Array.isArray(rows) ? rows : []).map((row) => ({
    ...newBranchDeliveryChargeRow(toText(row.fromCartValue)),
    toCartValue: row.isToInfinity === true ? '' : toText(row.toCartValue),
    isToInfinity: row.isToInfinity === true,
    baseKM: toText(row.baseKM),
    basePrice: toText(row.basePrice),
    pricePerKM: toText(row.pricePerKM),
  }));
}

/** The From that follows a row ending at `previousTo`, or '' while that To is not a valid amount. */
export function nextFromCartValue(previousTo) {
  const amount = parseAmount(previousTo);
  return amount === null ? '' : String((toPaise(amount) + 1) / 100);
}

/** Every problem in the table, as `{ row, field, message }` with a 1-based row (null for the whole table). */
export function validateBranchDeliveryChargeRows(rows) {
  const errors = [];
  const fail = (row, field, message) => errors.push({ row, field, message });

  if (!Array.isArray(rows) || rows.length === 0) {
    fail(null, null, "Add the cart-value rows: the first row starts at ₹0 and the last row's To is Infinity");
    return errors;
  }
  if (rows.length > MAX_BRANCH_DELIVERY_CHARGE_ROWS) {
    fail(null, null, `At most ${MAX_BRANCH_DELIVERY_CHARGE_ROWS} rows are allowed`);
    return errors;
  }

  const parsedRows = rows.map((row, index) => {
    const n = index + 1;
    const parsed = { isToInfinity: row.isToInfinity === true };
    ['fromCartValue', 'toCartValue', 'baseKM', 'basePrice', 'pricePerKM'].forEach((field) => {
      parsed[field] = null;
      if (field === 'toCartValue' && parsed.isToInfinity) return;
      const label = FIELD_LABELS[field];
      if (toText(row[field]).trim() === '') {
        fail(n, field, `Row ${n}: ${label} is required`);
        return;
      }
      parsed[field] = parseAmount(row[field]);
      if (parsed[field] === null) {
        const what = field === 'baseKM' ? 'a distance of 0 km or more' : '₹0 or more';
        fail(n, field, `Row ${n}: ${label} must be ${what} with at most 2 decimals`);
      }
    });
    if (!parsed.isToInfinity && parsed.fromCartValue !== null && parsed.toCartValue !== null && parsed.toCartValue <= parsed.fromCartValue) {
      fail(n, 'toCartValue', `Row ${n}: To must be greater than From`);
    }
    return parsed;
  });

  const first = parsedRows[0];
  if (first.fromCartValue !== null && first.fromCartValue !== 0) {
    fail(1, 'fromCartValue', 'Row 1: From must be ₹0 so that every cart value is covered');
  }
  if (first.isToInfinity) {
    fail(1, 'toCartValue', 'Row 1: To must be a cart value; Infinity can be chosen from row 2 onwards');
  }

  for (let i = 1; i < parsedRows.length; i++) {
    const previous = parsedRows[i - 1];
    const current = parsedRows[i];
    if (previous.isToInfinity) {
      fail(i, 'toCartValue', `Row ${i}: only the last row can have To = Infinity`);
      continue;
    }
    if (previous.toCartValue === null || current.fromCartValue === null) continue;
    const expectedFrom = (toPaise(previous.toCartValue) + 1) / 100;
    if (toPaise(current.fromCartValue) !== toPaise(expectedFrom)) {
      fail(i + 1, 'fromCartValue', `Row ${i + 1}: From must be ${formatRupees(expectedFrom)} (₹0.01 after Row ${i}'s To of ${formatRupees(previous.toCartValue)}) so the ranges neither overlap nor leave a gap`);
    }
  }

  const lastIndex = parsedRows.length - 1;
  if (!parsedRows[lastIndex].isToInfinity && lastIndex > 0) {
    fail(lastIndex + 1, 'toCartValue', `Row ${lastIndex + 1}: the last row's To must be Infinity so that every higher cart value is covered`);
  } else if (lastIndex === 0 && !first.isToInfinity) {
    fail(null, null, 'Add a second row whose To is Infinity so that every cart value is covered');
  }

  return errors;
}

/** Editable rows as the request body expects them; only call with rows that passed validation. */
export function branchDeliveryChargesPayload(rows) {
  return rows.map((row) => ({
    fromCartValue: parseAmount(row.fromCartValue),
    toCartValue: row.isToInfinity === true ? null : parseAmount(row.toCartValue),
    isToInfinity: row.isToInfinity === true,
    baseKM: parseAmount(row.baseKM),
    basePrice: parseAmount(row.basePrice),
    pricePerKM: parseAmount(row.pricePerKM),
  }));
}
